import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, img, errMsg } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { UploadButton } from "./ImageUploader";

const GROUPS = [
  ["Brand", [["logo_url", "Logo", "image"], ["announcement", "Announcement bar"]]],
  ["Homepage hero", [["hero_image", "Hero campaign image", "image"], ["hero_eyebrow", "Small heading"], ["hero_title", "Headline"], ["hero_subtitle", "Sub-text", "long"]]],
  ["Homepage intro & featured", [["intro_title", "Intro headline"], ["intro_text", "Intro text", "long"], ["editorial_image", "Featured collection image", "image"]]],
  ["About page", [["about_title", "Page headline"], ["about_story", "Brand story", "long"], ["about_inspiration", "Inspiration", "long"], ["about_vision", "Vision", "long"], ["about_image_1", "Large photograph", "image"], ["about_image_2", "Second photograph", "image"]]],
  ["Launch countdown", [["launch_at", "Launch date & time (leave empty to hide the countdown)", "datetime"], ["launch_title", "Countdown headline (e.g. The first collection arrives in)"]]],
  ["Delivery & shipping", [["delivery_badge", "Delivery time shown on product pages (e.g. UK delivery 2–4 days)"], ["delivery_details", "Delivery details (product page & shipping page)", "long"], ["processing_time", "Processing / dispatch time (shipping page)", "long"], ["business_address", "Your return address: street, town, postcode (printed on invoices & labels; brand name added automatically)", "long"]]],
  ["Social & contact", [["instagram_handle", "Instagram handle"], ["instagram_url", "Instagram link"], ["tiktok_url", "TikTok link"], ["pinterest_url", "Pinterest link"], ["contact_email", "Contact email"]]],
];

const Field = ({ k, label, type, value, onChange }) => (
  <div className="space-y-2">
    <span className="eyebrow text-[var(--mm-muted)] block">{label}</span>
    {type === "image" ? (
      <div className="flex items-center gap-4 flex-wrap">
        {value && <img src={img(value)} alt="" className="h-20 w-auto max-w-[140px] object-contain bg-[var(--mm-sand)]" />}
        <UploadButton testId={`settings-${k}-upload`} onUploaded={onChange} label="Upload" />
        <input data-testid={`settings-${k}-input`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or image URL" className="field-mm flex-1 min-w-[200px] py-2" />
      </div>
    ) : type === "datetime" ? (
      <div className="flex gap-3 items-center">
        <input data-testid={`settings-${k}-input`} type="datetime-local" value={value ? new Date(new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ""} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : "")} className="field-mm" />
        {value && <button type="button" data-testid={`settings-${k}-clear`} onClick={() => onChange("")} className="eyebrow underline">Clear</button>}
      </div>
    ) : type === "long" ? (
      <textarea data-testid={`settings-${k}-input`} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="field-mm resize-y" />
    ) : (
      <input data-testid={`settings-${k}-input`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="field-mm" />
    )}
  </div>
);

export default function SettingsTab() {
  const { setSettings } = useShop();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", s); setSettings(data); toast.success("Site content saved"); } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  if (!s) return null;
  return (
    <div data-testid="admin-settings-tab" className="max-w-3xl space-y-12">
      {GROUPS.map(([title, fields]) => (
        <section key={title}>
          <h2 className="font-serif-mm text-3xl mb-6">{title}</h2>
          <div className="space-y-6">{fields.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} value={s[k]} onChange={(v) => setS((p) => ({ ...p, [k]: v }))} />)}</div>
        </section>
      ))}
      <button data-testid="settings-save-button" onClick={save} disabled={saving} className="btn-mm sticky bottom-6">{saving ? "Saving…" : "Save site content"}</button>
    </div>
  );
}
